import React, { useState, useEffect, useMemo } from 'react';
import {
  LuSearch,
  LuCalendar,
  LuDownload,
  LuX,
  LuBookOpen,
  LuSchool,
  LuUsers,
  LuSparkles,
  LuTrophy,
  LuArrowRight
} from 'react-icons/lu';
import { PiChurchLight } from 'react-icons/pi';
import newsHero from '../../assets/News.png';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';

import ScrollNewsTicker from '../../components/ScrollNewsTicker';

// Helper to format Firestore Timestamp or Date strings (Safari & Cross-Browser Safe)
const formatDate = (timestamp) => {
  if (!timestamp) return 'Recent Update';
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  if (typeof timestamp === 'string') {
    const safeStr = timestamp.replace(/-/g, '/');
    const d = new Date(safeStr);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    return timestamp;
  }
  return 'Recent Update';
};

export default function NewsAndResults({ activeSection = 'news', onSelectSection = () => { } }) {
  const isInitialResults = activeSection === 'results' || activeSection === 'our results' || activeSection.includes('result');
  const [activeTab, setActiveTab] = useState(isInitialResults ? 'results' : 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResultModal, setSelectedResultModal] = useState(null);
  const [selectedNewsModal, setSelectedNewsModal] = useState(null);
  const [newsList, setNewsList] = useState([]);
  const [resultsList, setResultsList] = useState([]);
  const [loading, setLoading] = useState(true);

  const tabs = [
    { id: 'all', label: 'All Updates' },
    { id: 'news', label: 'Diocesan News' },
    { id: 'results', label: 'Official Results' },
    { id: 'announcements', label: 'Announcements' }
  ];

  // Fetch News and Results from Firestore simultaneously
  useEffect(() => {
    let isMounted = true;
    async function fetchAllData() {
      try {
        setLoading(true);

        // 1. Fetch News
        const newsPromise = getDocs(collection(db, 'news')).catch(() => null);

        // 2. Fetch Results (try 'result' first, then 'results')
        const resultsPromise = getDocs(collection(db, 'result'))
          .then(async (snap) => {
            if (snap.empty) {
              const altSnap = await getDocs(collection(db, 'results'));
              return altSnap;
            }
            return snap;
          })
          .catch(() => null);

        const [newsSnap, resultsSnap] = await Promise.all([newsPromise, resultsPromise]);

        // Process News
        const fetchedNews = [];
        if (newsSnap && !newsSnap.empty) {
          newsSnap.forEach((doc) => {
            const data = doc.data();
            const title = data.title || 'Diocesan News';
            const content = data.content || data.description || '';
            const imgUrl = data.img || data.image || '';
            const dateStr = formatDate(data.time_stamp || data.timestamp || data.date);
            const order = typeof data.order === 'number' ? data.order : 999;

            fetchedNews.push({
              id: doc.id,
              type: 'news',
              badge: 'Diocesan News',
              title,
              description: content,
              content,
              image: imgUrl,
              date: dateStr,
              order,
              raw: data
            });
          });
        }

        // Process Results
        const fetchedResults = [];
        if (resultsSnap && !resultsSnap.empty) {
          resultsSnap.forEach((doc) => {
            const data = doc.data();
            const contestName = data.contest || data.title || data.name || 'Official Competition Results';
            const dateStr = formatDate(data.date || data.time_stamp || data.timestamp);
            const order = typeof data.order === 'number' ? data.order : 999;
            const pdfUrl = data.url || data.pdfUrl || data.pdf_url || '';
            const prizeList = Array.isArray(data.prize) ? data.prize : [];

            fetchedResults.push({
              id: doc.id,
              type: 'results',
              badge: 'Official Results',
              title: contestName,
              contest: contestName,
              date: dateStr,
              order,
              url: pdfUrl,
              prize: prizeList,
              description: data.description || `Official results, scoreboard, and prize standings for ${contestName}.`,
              raw: data
            });
          });
        }

        if (isMounted) {
          // Sort items by order
          fetchedNews.sort((a, b) => a.order - b.order);
          fetchedResults.sort((a, b) => a.order - b.order);

          setNewsList(fetchedNews);
          setResultsList(fetchedResults);
        }
      } catch (error) {
        console.error("Error fetching data from Firebase:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchAllData();
    return () => { isMounted = false; };
  }, []);

  // Combined content list
  const combinedContent = useMemo(() => {
    return [...resultsList, ...newsList];
  }, [resultsList, newsList]);

  // Filter & Search Logic
  const filteredContent = useMemo(() => {
    return combinedContent.filter(item => {
      const matchesTab = activeTab === 'all' || item.type === activeTab;
      const q = searchQuery.toLowerCase();
      const matchesSearch = searchQuery === '' ||
        (item.title && item.title.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q)) ||
        (item.badge && item.badge.toLowerCase().includes(q));
      return matchesTab && matchesSearch;
    });
  }, [activeTab, searchQuery, combinedContent]);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2D2420] font-sans pb-16">

      {/* ── 1. Hero Header Banner ── */}
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={newsHero}
            alt="CSI Diocesan News & Results"
            className="w-full h-full object-cover object-center filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">

          <div className="max-w-xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              NEWS &amp; EVENTS
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              Stay informed with official diocesan circulars, housing project dedications, Sunday School talent competitions, and verified scoreboards.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white font-semibold">News &amp;Events</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuTrophy className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">
                  {resultsList.length > 0 ? `${resultsList.length}` : 'Annual'}
                </span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Official Results</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuCalendar className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">
                  {newsList.length > 0 ? `${newsList.length}` : 'Live'}
                </span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">News Articles</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuSparkles className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">Verified</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Scoreboards</span>
              </div>
            </div>
          </div>

        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* ── Dynamic Diocesan Scroll News Ticker (from Firestore 'scrollnews') ── */}
      <ScrollNewsTicker onSelectSection={onSelectSection} />

      {/* Main Content Area */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-8">

        {/* ── 2. Filter Tabs & Search Bar ── */}
        <div className="bg-white rounded-[24px] shadow-[0_10px_35px_rgba(0,0,0,0.04)] p-5 sm:p-6 border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`text-xs font-semibold px-4 py-2.5 rounded-xl transition-all whitespace-nowrap cursor-pointer ${activeTab === tab.id
                  ? 'bg-[#842343] text-white shadow-sm'
                  : 'bg-[#FAF7F2] text-[#5A4F4A] hover:bg-gray-100 border border-gray-200/80'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-80">
            <input
              type="text"
              placeholder="Search news, contests, or results..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#fdfaf7] border border-gray-200 rounded-xl pl-4 pr-10 py-2.5 text-[13px] text-[#1a1715] placeholder:text-[#9e918b] outline-none focus:border-[#842343] transition-colors"
            />
            <LuSearch className="w-4 h-4 text-[#8A7E79] absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {filteredContent.map((item) => {
            const isResult = item.type === 'results';

            return (
              <div
                key={item.id}
                className="bg-white rounded-[24px] overflow-hidden border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_30px_rgba(132,35,67,0.08)] transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1 h-full"
              >
                <div className="flex flex-col flex-1">
                  {/* Header: Cover Image OR Styled Trophy Header */}
                  {item.image ? (
                    <div className="relative h-48 w-full overflow-hidden bg-gray-100 shrink-0">
                      <img
                        src={item.image}
                        alt={item.title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1548625149-fc4a29cf7092?auto=format&fit=crop&q=80&w=600';
                        }}
                        className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700"
                      />
                      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-xs text-[#842343] font-bold text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">
                        {item.badge}
                      </div>
                    </div>
                  ) : (
                    <div className="relative h-24 w-full bg-gradient-to-r from-[#FAF6F2] to-[#F7EFEA] p-5 flex items-center justify-between border-b border-gray-100 shrink-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-[#7B1E3A] text-[#D4A64A] flex items-center justify-center shadow-xs">
                          <LuTrophy className="w-5 h-5" />
                        </div>
                        <span className="font-serif font-bold text-[#7B1E3A] text-sm">
                          Competition Scoreboard
                        </span>
                      </div>
                      <span className="bg-[#842343]/10 text-[#842343] font-bold text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#842343]/20">
                        {item.badge}
                      </span>
                    </div>
                  )}

                  {/* Card Body with strict baseline alignment */}
                  <div className="p-5 flex flex-col flex-1">
                    {/* Date Row */}
                    <div className="flex items-center gap-1.5 text-[11px] text-[#8A7E79] font-medium mb-2">
                      <LuCalendar className="w-3.5 h-3.5 text-[#842343] shrink-0" />
                      <span>{item.date}</span>
                    </div>

                    {/* Title with Fixed Height (2 lines clamp) */}
                    <h3
                      className="font-serif font-bold text-[#1a1715] text-[16px] leading-snug mb-2 group-hover:text-[#842343] transition-colors line-clamp-2 min-h-[44px] flex items-center"
                      title={item.title}
                    >
                      {item.title}
                    </h3>

                    {/* Description with Fixed Height (3 lines clamp) */}
                    <p
                      className="text-[12.5px] text-[#5A4F4A] leading-relaxed mb-4 line-clamp-3 min-h-[56px]"
                      title={item.description}
                    >
                      {item.description}
                    </p>

                    {/* Prize Standings Preview (For Results) */}
                    {isResult && item.prize && item.prize.length > 0 && (
                      <div className="bg-[#FAF7F2] rounded-xl p-3 space-y-1.5 mb-2 text-[11px] border border-[#E8DDD7]/60 mt-auto">
                        <span className="text-[10px] font-bold text-[#8A7E79] uppercase tracking-wider block mb-1">
                          Prize Standings
                        </span>
                        {item.prize.slice(0, 2).map((sc, i) => {
                          if (typeof sc === 'object' && sc !== null) {
                            return (
                              <div key={i} className="flex justify-between border-b border-gray-100 last:border-0 pb-1">
                                <span className="text-[#5A4F4A] truncate max-w-[65%]">{sc.category || sc.name || sc.title || `Prize ${i + 1}`}</span>
                                <strong className="text-[#842343]">{sc.winner || sc.award || sc.rank || 'Winner'}</strong>
                              </div>
                            );
                          }
                          return (
                            <div key={i} className="text-[#5A4F4A] text-[11px]">
                              • {String(sc)}
                            </div>
                          );
                        })}
                        {item.prize.length > 2 && (
                          <span className="text-[10px] text-[#842343] font-semibold block pt-0.5">
                            + {item.prize.length - 2} more categories
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Actions Footer (Always Aligned at Bottom) */}
                <div className="px-5 pb-5 pt-3 flex items-center justify-between gap-2 border-t border-gray-100">
                  <button
                    onClick={() => {
                      if (isResult) {
                        setSelectedResultModal(item);
                      } else {
                        setSelectedNewsModal(item);
                      }
                    }}
                    className="text-[#842343] font-bold text-xs uppercase tracking-wider flex items-center gap-1 group/btn cursor-pointer hover:underline"
                  >
                    <span>{isResult ? 'View Scoreboard' : 'Read Full Story'}</span>
                    <LuArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" />
                  </button>

                  {isResult && item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FAF6F2] hover:bg-[#842343] text-[#842343] hover:text-white border border-[#E8DDD7] text-[11px] font-semibold transition-colors"
                    >
                      <LuDownload className="w-3 h-3" />
                      <span>PDF</span>
                    </a>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      </div>

      {/* ── Modal for News Articles (Full Story) ── */}
      {selectedNewsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 relative flex flex-col">

            {selectedNewsModal.image && (
              <div className="relative h-64 w-full bg-gray-100 shrink-0">
                <img
                  src={selectedNewsModal.image}
                  alt={selectedNewsModal.title}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1548625149-fc4a29cf7092?auto=format&fit=crop&q=80&w=600';
                  }}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                <button
                  onClick={() => setSelectedNewsModal(null)}
                  className="absolute top-4 right-4 w-9 h-9 bg-white/80 hover:bg-white text-gray-800 rounded-full flex items-center justify-center shadow-lg transition-colors cursor-pointer"
                >
                  <LuX className="w-5 h-5" />
                </button>

                <div className="absolute bottom-4 left-6 right-6 text-white">
                  <span className="bg-[#842343] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full mb-2 inline-block">
                    {selectedNewsModal.badge}
                  </span>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold leading-tight drop-shadow-sm">
                    {selectedNewsModal.title}
                  </h2>
                </div>
              </div>
            )}

            <div className="p-6 sm:p-8 space-y-4">
              {!selectedNewsModal.image && (
                <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-widest text-[#842343] block mb-1">
                      {selectedNewsModal.badge}
                    </span>
                    <h2 className="font-serif font-bold text-xl text-[#1a1715]">
                      {selectedNewsModal.title}
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedNewsModal(null)}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 cursor-pointer"
                  >
                    <LuX className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-[#8A7E79] font-medium pb-2 border-b border-gray-100">
                <LuCalendar className="w-4 h-4 text-[#842343]" />
                <span>Published on {selectedNewsModal.date}</span>
              </div>

              <div className="text-sm text-[#3D332E] leading-relaxed whitespace-pre-line font-sans pt-2">
                {selectedNewsModal.content || selectedNewsModal.description}
              </div>

              <div className="border-t border-gray-100 pt-5 flex justify-end">
                <button
                  onClick={() => setSelectedNewsModal(null)}
                  className="bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-semibold py-2.5 px-6 rounded-xl shadow-xs transition-colors cursor-pointer"
                >
                  Close Article
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ── Modal for Official Results & Scoreboard ── */}
      {selectedResultModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl border border-gray-100 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setSelectedResultModal(null)}
              className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 cursor-pointer"
            >
              <LuX className="w-4 h-4" />
            </button>

            <span className="text-xs font-bold uppercase tracking-widest text-[#842343] block mb-1">Official Result Board</span>
            <h3 className="font-serif font-bold text-[#1a1715] text-[22px] sm:text-[24px] mb-2">
              {selectedResultModal.contest || selectedResultModal.title}
            </h3>

            <div className="flex items-center gap-2 text-xs text-[#8A7E79] font-medium mb-5">
              <LuCalendar className="w-3.5 h-3.5 text-[#842343]" />
              <span>Announced: {selectedResultModal.date}</span>
            </div>

            {/* Prize Roster */}
            {selectedResultModal.prize && selectedResultModal.prize.length > 0 ? (
              <div className="space-y-3 mb-6">
                <span className="text-xs font-bold uppercase tracking-wider text-[#8A7E79] block">
                  Category Winners &amp; Standings
                </span>
                {selectedResultModal.prize.map((item, idx) => {
                  if (typeof item === 'object' && item !== null) {
                    return (
                      <div key={idx} className="p-3.5 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7]/70 flex items-center justify-between gap-3">
                        <div>
                          <span className="text-xs text-[#8A7E79] font-medium block">
                            {item.category || item.name || item.title || `Category ${idx + 1}`}
                          </span>
                          <strong className="text-sm font-serif text-[#1a1715]">
                            {item.winner || item.award || item.parish || 'Winner'}
                          </strong>
                        </div>
                        {(item.points || item.rank || item.award) && (
                          <span className="bg-[#842343] text-white text-xs font-bold px-3 py-1 rounded-full shadow-2xs">
                            {item.points || item.rank || item.award}
                          </span>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={idx} className="p-3 rounded-xl bg-[#FAF7F2] border border-[#E8DDD7]/70 text-xs font-semibold text-[#2D2420]">
                      {String(item)}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-[#5A4F4A] mb-6 leading-relaxed">
                {selectedResultModal.description}
              </p>
            )}

            {/* Modal Actions Footer */}
            <div className="border-t border-gray-100 pt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              {selectedResultModal.url ? (
                <a
                  href={selectedResultModal.url}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-semibold shadow-xs transition-colors"
                >
                  <LuDownload className="w-4 h-4" />
                  <span>Download Official Result PDF</span>
                </a>
              ) : (
                <div />
              )}

              <button
                onClick={() => setSelectedResultModal(null)}
                className="w-full sm:w-auto border border-gray-200 text-[#5A4F4A] text-xs font-semibold py-2.5 px-6 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 4. Bottom Statistics Strip ── */}
      <section className="mt-14 pt-8 pb-8 bg-[#f5ede5]/70 border-t border-[#ede2d6]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 text-center">

            <div className="flex items-center justify-center gap-3">
              <PiChurchLight className="w-7 h-7 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">26+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Dioceses</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuBookOpen className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">5</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Districts</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuSchool className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">120+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Institutions</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">49</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Presbyters</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">4.5M+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Members</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <span className="text-[#842343] text-xl font-serif">✝</span>
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">100+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Worship Services</span>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
